import {Trans} from '@ui/i18n/trans';
import {CartItem, useSaveDepositChoice} from './checkout-queries';
import {money} from '@app/student/portal/portal-format';

/**
 * Pay a package's deposit now, or all of it.
 *
 * Two plain choices with the figure on each, rather than a toggle labelled
 * "deposit" — what a student is deciding is how much leaves their account
 * today, so that is what each option says.
 *
 * Shared by the cart drawer and the checkout page so the two can never offer
 * different amounts for the same line.
 */
export function DepositChoice({item, currency}: {item: CartItem; currency: string}) {
  const save = useSaveDepositChoice();

  if (!item.deposit_percentage) {
    return null;
  }

  const full = item.price * item.quantity;
  // What the deposit WOULD be if chosen. When it is not chosen `due_now` is
  // the full price, so it cannot be read off the line; this mirrors the
  // server's round-up to a whole unit.
  const depositNow = Math.min(
    full,
    Math.ceil((full * item.deposit_percentage) / 100 / 100) * 100,
  );

  const option = (payDeposit: boolean, title: React.ReactNode, amount: number, hint: React.ReactNode) => {
    const on = item.pay_deposit === payDeposit;
    return (
      <label
        className={`flex cursor-pointer items-start gap-8 rounded-lg border p-10 transition ${
          on ? 'border-primary bg-primary/5' : 'border-divider hover:border-primary'
        }`}
      >
        <input
          type="radio"
          checked={on}
          disabled={save.isPending}
          className="mt-2 shrink-0 accent-primary"
          onChange={() => save.mutate({itemId: item.id, payDeposit})}
        />
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold">{title}</span>
          <span className="block text-2xs text-muted">{hint}</span>
        </span>
        <span className="shrink-0 text-sm font-bold text-primary" dir="ltr">
          {money(amount / 100, currency)}
        </span>
      </label>
    );
  };

  return (
    <div className="mt-10 space-y-6">
      {option(
        true,
        <Trans message="Pay a :pct% deposit now" values={{pct: item.deposit_percentage}} />,
        depositNow,
        <Trans
          message="The rest, :amount, within 30 days. We start as soon as the deposit arrives."
          values={{amount: money((full - depositNow) / 100, currency)}}
        />,
      )}
      {option(false, <Trans message="Pay in full now" />, full, <Trans message="Nothing more to pay later." />)}
    </div>
  );
}
